import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { LcncAppPicker } from './LcncAppPicker.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface FormRow {
  readonly formId: string;
  readonly formCode: string;
  readonly formName: string;
  readonly formType: string;
  readonly status: string;
  readonly versionNo: number;
}

interface FieldRow {
  readonly fieldId: string;
  readonly fieldCode: string;
  readonly fieldLabel: string;
  readonly controlType: string;
  readonly isMandatory: boolean;
}

const FORM_TYPES = ['', 'STANDARD', 'WIZARD', 'GRID', 'SURVEY', 'INSPECTION', 'APPROVAL'];
const STATUSES = ['', 'DRAFT', 'TESTING', 'PUBLISHED', 'ARCHIVED'];
const CREATE_TYPES = FORM_TYPES.filter(Boolean);
const CONTROL_TYPES = [
  'TEXT', 'TEXTAREA', 'NUMBER', 'DATE', 'DATETIME', 'DROPDOWN', 'RADIO', 'CHECKBOX',
  'MULTISELECT', 'FILE', 'EMAIL', 'PHONE', 'CURRENCY', 'TOGGLE', 'SLIDER', 'RATING',
  'SIGNATURE', 'GEOLOCATION', 'BARCODE', 'GRID', 'FORMULA', 'LOOKUP', 'SECTION', 'HTML',
];

/**
 * M28 — Form Designer (§7 / §8 / §9 / §32). Drag-and-drop form catalogue for the
 * 24-control palette (§7 + §8 advanced GRID/FORMULA/LOOKUP) and the nine §9
 * validation strategies. Lists every form_definition (T366) with its archetype,
 * lifecycle status and version, filterable by form type and status. Supports
 * create → add fields (the control palette) → validate (§9 gate) → publish,
 * each wired to the guarded /v1/lcnc/forms write API.
 */
export function M28FormDesignerPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<FormRow[]>([]);
  const [formType, setFormType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [applicationId, setApplicationId] = useState('');
  const [name, setName] = useState('');
  const [newType, setNewType] = useState('STANDARD');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [openFieldsFor, setOpenFieldsFor] = useState<string | null>(null);
  const [fields, setFields] = useState<FieldRow[]>([]);
  const [fieldLabel, setFieldLabel] = useState('');
  const [controlType, setControlType] = useState('TEXT');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (formType) params.set('formType', formType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: FormRow[] }>(`/v1/lcnc/forms${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, formType, status, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m28.formDesigner.notAuthorised', { verb, perm })
      : (err as Error).message;

  const runRowAction = async (fn: () => Promise<unknown>, successMsg: string, verb: string, perm: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!applicationId.trim()) { setActionError(tr('m28.formDesigner.validationApplicationId')); return; }
    if (!name.trim()) { setActionError(tr('m28.formDesigner.validationName')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: FormRow }>(`/v1/lcnc/forms`, {
        method: 'POST',
        body: { applicationId: applicationId.trim(), formName: name.trim(), formType: newType },
        token,
      });
      setNotice(tr('m28.formDesigner.noticeCreated', { code: r.data.formCode }));
      setName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m28.formDesigner.verbCreate'), 'FORM_DESIGN'));
    } finally {
      setBusy(false);
    }
  };

  const validate = (f: FormRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/forms/${f.formId}/validate`, { method: 'POST', body: {}, token }),
      tr('m28.formDesigner.noticeValidated', { code: f.formCode }), tr('m28.formDesigner.verbValidate'), 'FORM_DESIGN',
    );

  const publish = (f: FormRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/forms/${f.formId}/publish`, { method: 'POST', body: {}, token }),
      tr('m28.formDesigner.noticePublished', { code: f.formCode }), tr('m28.formDesigner.verbPublish'), 'FORM_DESIGN',
    );

  const loadFields = async (f: FormRow): Promise<void> => {
    if (openFieldsFor === f.formId) { setOpenFieldsFor(null); return; }
    setActionError(null);
    setOpenFieldsFor(f.formId);
    try {
      const r = await apiRequest<{ data: FieldRow[] }>(`/v1/lcnc/forms/${f.formId}/fields`, { token });
      setFields(r.data);
    } catch (err) {
      setActionError(failMessage(err, tr('m28.formDesigner.verbViewFields'), 'APP_VIEW'));
      setFields([]);
    }
  };

  const refreshFields = async (formId: string): Promise<void> => {
    try {
      const r = await apiRequest<{ data: FieldRow[] }>(`/v1/lcnc/forms/${formId}/fields`, { token });
      setFields(r.data);
    } catch { /* surfaced elsewhere */ }
  };

  const addField = async (f: FormRow): Promise<void> => {
    if (!fieldLabel.trim()) { setActionError(tr('m28.formDesigner.validationFieldLabel')); return; }
    await runRowAction(
      () => apiRequest(`/v1/lcnc/forms/${f.formId}/fields`, {
        method: 'POST', body: { fieldLabel: fieldLabel.trim(), controlType }, token,
      }),
      tr('m28.formDesigner.noticeAddedField', { code: f.formCode }), tr('m28.formDesigner.verbAddFields'), 'FORM_DESIGN',
    );
    setFieldLabel('');
    if (openFieldsFor === f.formId) await refreshFields(f.formId);
  };

  const deleteField = async (f: FormRow, field: FieldRow): Promise<void> => {
    await runRowAction(
      () => apiRequest(`/v1/lcnc/forms/${f.formId}/fields/${field.fieldId}`, { method: 'DELETE', token }),
      tr('m28.formDesigner.noticeRemovedField', { label: field.fieldLabel }), tr('m28.formDesigner.verbDeleteFields'), 'FORM_DESIGN',
    );
    if (openFieldsFor === f.formId) await refreshFields(f.formId);
  };

  return (
    <main aria-label="form-designer">
      <h1>{tr('m28.formDesigner.title')}</h1>

      <section aria-label="create-form">
        <h2>{tr('m28.formDesigner.newHeading')}</h2>
        <form aria-label="form-form" onSubmit={(e) => void create(e)}>
          <LcncAppPicker label={tr('m28.formDesigner.fieldApplicationId')} value={applicationId} onPick={setApplicationId} />
          <label>{tr('m28.formDesigner.fieldName')}<input aria-label="field-form-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{tr('m28.formDesigner.fieldType')}
            <select aria-label="field-form-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-form" disabled={busy}>{busy ? tr('m28.formDesigner.creating') : tr('m28.formDesigner.create')}</button>
        </form>
        {actionError && <p role="alert" aria-label="form-error">{actionError}</p>}
        {notice && <p aria-label="form-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m28.formDesigner.filterType')}
          <select aria-label="filter-form-type" value={formType} onChange={(e) => setFormType(e.target.value)}>
            {FORM_TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m28.formDesigner.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m28.formDesigner.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m28.formDesigner.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m28.formDesigner.loading')}</p> : (
        <>
          <p aria-label="form-count">{tr('m28.formDesigner.count', { count: rows.length })}</p>
          <table aria-label="form-table">
            <thead><tr><th>{tr('m28.formDesigner.colCode')}</th><th>{tr('m28.formDesigner.colName')}</th><th>{tr('m28.formDesigner.colType')}</th><th>{tr('m28.formDesigner.colStatus')}</th><th>{tr('m28.formDesigner.colVersion')}</th><th>{tr('m28.formDesigner.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((f) => (
                <tr key={f.formId}>
                  <td>{f.formCode}</td><td>{f.formName}</td><td>{f.formType}</td>
                  <td>{f.status}</td><td>v{f.versionNo}</td>
                  <td>
                    <button type="button" aria-label={`fields-${f.formCode}`} onClick={() => void loadFields(f)}>{tr('m28.formDesigner.btnFields')}</button>
                    <button type="button" aria-label={`validate-${f.formCode}`} onClick={() => void validate(f)}>{tr('m28.formDesigner.btnValidate')}</button>
                    <button type="button" aria-label={`publish-${f.formCode}`} onClick={() => void publish(f)}>{tr('m28.formDesigner.btnPublish')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {openFieldsFor && (() => {
            const form = rows.find((r) => r.formId === openFieldsFor);
            if (!form) return null;
            return (
              <section aria-label="field-palette">
                <h2>{tr('m28.formDesigner.fieldsHeading', { code: form.formCode })}</h2>
                <div>
                  <label>{tr('m28.formDesigner.fieldFieldLabel')}<input aria-label="field-field-label" value={fieldLabel} onChange={(e) => setFieldLabel(e.target.value)} /></label>
                  <label>{tr('m28.formDesigner.fieldControl')}
                    <select aria-label="field-control-type" value={controlType} onChange={(e) => setControlType(e.target.value)}>
                      {CONTROL_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                  <button type="button" aria-label="add-field" onClick={() => void addField(form)}>{tr('m28.formDesigner.btnAddField')}</button>
                </div>
                <table aria-label="field-table">
                  <thead><tr><th>{tr('m28.formDesigner.fColLabel')}</th><th>{tr('m28.formDesigner.fColControl')}</th><th>{tr('m28.formDesigner.fColMandatory')}</th><th>{tr('m28.formDesigner.fColActions')}</th></tr></thead>
                  <tbody>
                    {fields.length === 0 ? (
                      <tr><td colSpan={4}>{tr('m28.formDesigner.noFields')}</td></tr>
                    ) : fields.map((field) => (
                      <tr key={field.fieldId}>
                        <td>{field.fieldLabel}</td><td>{field.controlType}</td><td>{field.isMandatory ? tr('m28.formDesigner.yes') : tr('m28.formDesigner.no')}</td>
                        <td><button type="button" aria-label={`delete-field-${field.fieldCode}`} onClick={() => void deleteField(form, field)}>{tr('m28.formDesigner.btnDelete')}</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            );
          })()}
        </>
      )}
    </main>
  );
}
